/** tm/components/ConsultStaffTodo.tsx — 상담 결과 입력 중 「직원에게 일일 업무 보내기」 (2026-10-12)
 *
 *  지도사 말: 「상담 마치면 갓길에 차 세우고 결과 입력 · 여기서 직원에게 일 배정할 게 있으면 바로 배정」
 *  · 일일 업무(/api/todos)로 바로 보낸다 — 상담 「저장」과 별개(저장 안 하고 닫아도 보낸 일은 남음)
 *  · 담당자 → 할 일 → 기한 순 · 직원이 한 명뿐이면 미리 골라 둠 · 03 해야할 일에서 한 번에 가져오기
 *  · 제목 앞에 [업체명] · 자세히 끝에 「○○ 상담(날짜) 결과에서」 · 보낸 것은 아래 줄로 남고 「보내기 취소」 가능
 */
import { useEffect, useRef, useState } from 'react';
import { C, T, R } from './ui';
import * as todosApi from '../api-todos';
import type { Staff } from './AssigneeChipPicker';
import type { ReportTodo } from '../api-report';

interface Props {
  companyName: string;
  consultDate: string;      // YYYY-MM-DD…
  reportTodos: ReportTodo[];
  onToast: (msg: string, type?: string) => void;
}
interface Sent { id: string; to: string; title: string; due: string }

const WD = ['일', '월', '화', '수', '목', '금', '토'];
function kstToday(): string { return new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10); }
function addDays(d: string, n: number): string { const t = new Date(d + 'T00:00:00Z'); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); }
function mdw(d: string): string { if (!d) return ''; const t = new Date(d.slice(0, 10) + 'T00:00:00Z'); return `${t.getUTCMonth() + 1}/${t.getUTCDate()}(${WD[t.getUTCDay()]})`; }
function myAccountId(): string {
  try { const a = JSON.parse(localStorage.getItem('account') || 'null') as { id?: string } | null; return (a && a.id) || ''; } catch { return ''; }
}

const chip = (on: boolean): React.CSSProperties => ({
  height: 34, padding: '0 12px', borderRadius: 8, border: `1.5px solid ${on ? C.accent : C.border}`, background: on ? C.accentLt : '#fff',
  color: on ? C.accent : '#374151', fontSize: T.xs, fontWeight: on ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
});
const one: React.CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 6 };

export default function ConsultStaffTodo({ companyName, consultDate, reportTodos, onToast }: Props) {
  const myId = myAccountId();
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [to, setTo] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [due, setDue] = useState(() => addDays(kstToday(), 1));   // 기본 = 내일(오늘이면 바로 「지남」이 되기 쉬움)
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<Sent[]>([]);
  const titleRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setStaff(null);
    todosApi.getStaff().then(r => {
      const list = r.data || [];
      setStaff(list);
      const others = list.filter(s => s.id !== myId);
      if (others.length === 1) setTo(p => (p.length ? p : [others[0].id]));   // 직원이 한 명이면 미리 골라 둠
    }).catch(() => setStaff([]));
  };
  useEffect(load, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const people = (staff || []).slice().sort((a, b) => (a.id === myId ? 1 : b.id === myId ? -1 : 0));   // 직원 먼저, 「나」는 뒤
  const nameOf = (id: string) => (id === myId ? '나' : ((staff || []).find(s => s.id === id) || { name: '' }).name);
  const toggle = (id: string) => setTo(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const fromReport = reportTodos.map(r => (r.task || '').trim()).filter(Boolean).filter((t, i, a) => a.indexOf(t) === i).slice(0, 6);
  const missing = !to.length ? '담당자를 고르세요' : !title.trim() ? '할 일을 적어 주세요' : '';
  const others = to.filter(x => x !== myId);
  const toName = to.length === 1 ? (to[0] === myId ? '내 일일 업무' : nameOf(to[0])) : `${nameOf(others[0] || to[0])} 외 ${to.length - 1}명`;
  const btn = busy ? '보내는 중' : missing || (to.length === 1 && to[0] === myId ? '내 일일 업무에 넣기' : `${toName}에게 보내기`);
  const prefix = companyName ? `[${companyName}] ` : '';
  const today = kstToday();
  const dues: { k: string; t: string }[] = [
    { k: today, t: '오늘' }, { k: addDays(today, 1), t: '내일' }, { k: addDays(today, 3), t: '+3일' }, { k: addDays(today, 7), t: '+7일' },
  ];

  async function send() {
    if (busy || missing) { if (missing) { onToast(missing, 'error'); if (to.length) titleRef.current?.focus(); } return; }
    setBusy(true);
    try {
      const from = `— ${companyName || '업체'} 상담(${mdw(consultDate)}) 결과에서`;
      const r = await todosApi.createTodo({ title: prefix + title.trim(), description: (desc.trim() ? desc.trim() + '\n\n' : '') + from, assignees: to, due_date: due || null });
      if (!r.success || !r.data) throw new Error(r.error || '보내지 못했어요');
      const id = r.data.id;
      setSent(p => [...p, { id, to: toName, title: title.trim(), due }]);
      onToast(to.length === 1 && to[0] === myId ? '내 일일 업무에 넣었어요' : `${toName}에게 보냈어요 · 일일 업무에 올라가요`, 'success');
      setTitle(''); setDesc('');
      titleRef.current?.focus();
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '보내지 못했어요', 'error'); }
    finally { setBusy(false); }
  }
  async function cancel(s: Sent) {
    try {
      const r = await todosApi.deleteTodo(s.id);
      if (!r.success) throw new Error(r.error || '취소하지 못했어요');
      setSent(p => p.filter(x => x.id !== s.id));
      onToast('보낸 일을 취소했어요', 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '취소하지 못했어요', 'error'); }
  }

  return (
    <div id="cr-staff-todo" style={{ border: `1px solid ${C.border}`, borderRadius: R.md, padding: 14, background: '#FAFBFC', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text, ...one }}>👤 직원에게 일일 업무 보내기</span>
        <span style={{ fontSize: T.xs, color: C.muted, flexShrink: 0 }}>저장과 별개로 바로 보내져요</span>
      </div>

      <div style={lbl}>담당자 <span style={{ color: C.red }}>*</span></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {staff === null && <span style={{ fontSize: T.xs, color: C.muted }}>담당자 불러오는 중…</span>}
        {staff !== null && people.length === 0 && (
          <span style={{ fontSize: T.xs, color: C.sub }}>담당자 목록을 못 불러왔어요 <button type="button" onClick={load} style={{ border: 'none', background: 'none', color: C.accent, fontWeight: 700, cursor: 'pointer', fontSize: T.xs }}>다시 불러오기</button></span>
        )}
        {people.map(s => {
          const on = to.includes(s.id);
          return <button key={s.id} type="button" aria-pressed={on} onClick={() => toggle(s.id)} style={{ ...chip(on), maxWidth: 160, ...one }}>{on ? '✓ ' : ''}{s.id === myId ? '나' : s.name}</button>;
        })}
      </div>

      <div style={lbl}>할 일 <span style={{ color: C.red }}>*</span></div>
      {fromReport.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: T.xs, color: C.muted, alignSelf: 'center', flexShrink: 0 }}>03에서 가져오기</span>
          {fromReport.map(t => (
            <button key={t} type="button" title={t} onClick={() => { setTitle(t); titleRef.current?.focus(); }}
              style={{ ...chip(title.trim() === t), height: 28, maxWidth: 220, ...one }}>{t}</button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff', marginBottom: 12, minWidth: 0 }}>
        {prefix && <span title={prefix} style={{ fontSize: T.xs, color: C.muted, padding: '0 0 0 10px', maxWidth: 160, flexShrink: 0, ...one }}>{prefix}</span>}
        <input ref={titleRef} value={title} onChange={e => setTitle(e.target.value)} maxLength={200}
          onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); } }}
          placeholder="예: 김포시 자금 잔여예산 확인" style={{ flex: 1, minWidth: 0, padding: '9px 10px', border: 'none', outline: 'none', fontSize: T.sm, background: 'transparent' }} />
      </div>

      <div style={lbl}>기한</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {dues.map(d => <button key={d.t} type="button" aria-pressed={due === d.k} onClick={() => setDue(d.k)} style={chip(due === d.k)}>{d.t}</button>)}
        <input type="date" value={due} onChange={e => setDue(e.target.value)} aria-label="다른 날"
          style={{ height: 34, padding: '0 8px', borderRadius: 8, border: `1px solid ${dues.some(d => d.k === due) ? C.border : C.accent}`, fontSize: T.xs, color: '#374151' }} />
        <span style={{ fontSize: T.xs, color: C.sub }}>{due ? `${mdw(due)}까지` : '기한 없음'}</span>
      </div>

      <div style={lbl}>자세히 (선택)</div>
      <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} maxLength={2000} placeholder="참고할 것 · 연락처 등"
        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: T.sm, lineHeight: 1.5, resize: 'vertical', outline: 'none', marginBottom: 12 }} />

      <button type="button" onClick={() => void send()} aria-disabled={!!missing || busy}
        style={{ width: '100%', height: 42, borderRadius: 8, border: 'none', cursor: busy ? 'default' : 'pointer', fontSize: T.sm, fontWeight: 700,
          background: missing || busy ? '#E5E7EB' : C.accent, color: missing || busy ? '#6B7280' : '#fff', ...one }}>{btn}</button>

      {sent.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'grid', gap: 6 }}>
          <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub }}>보낸 일 {sent.length}건</div>
          {sent.map(s => (
            <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '88px minmax(0,1fr) 72px auto', gap: 8, alignItems: 'center', fontSize: T.xs }}>
              <span style={{ color: '#059669', fontWeight: 700, ...one }}>✓ {s.to}</span>
              <span title={s.title} style={{ color: C.text, ...one }}>{s.title}</span>
              <span style={{ color: C.sub, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.due ? mdw(s.due) : '기한 없음'}</span>
              <button type="button" onClick={() => void cancel(s)} style={{ border: 'none', background: 'none', color: C.sub, textDecoration: 'underline', cursor: 'pointer', fontSize: T.xs, padding: 0 }}>보내기 취소</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
